import { useState } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import CloseIcon from "../../../assets/close.svg?react";
import MinimizeIcon from "../../../assets/minimize.svg?react";
import NfsButton from "../NfsButton/NfsButton";
import RenderInWindow from "../../RenderInWindow/RenderInWindow";
import "./FloatingWindow.css";

interface FloatingWindowProps {
    toolRef: React.RefObject<HTMLDivElement>;
    closeWindow: () => void;
    children: React.ReactNode;
    Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    title?: string;
    className?: string;
    contentClassName?: string;
    defaultPosition?: { x: number; y: number };
    width?: number;
    /** Renders the window as a modal: dimmed backdrop over the app, and no dragging. */
    backdrop?: boolean;
    /** Work is in progress: the window is shown but nothing in it can be used until it ends. */
    busy?: boolean;
    /** The user can resize the window from its lower right corner. Not in the popout, which the browser sizes. */
    resizable?: boolean;
    /** Adds a header button that collapses the window to its header. Not in the popout. */
    minimizable?: boolean;
}

const FloatingWindow = (props: FloatingWindowProps) => {
    const { toolRef, closeWindow, children, Icon, title, className, contentClassName, defaultPosition, width } = props;
    const backdrop = props.backdrop ?? false;
    const busy = props.busy ?? false;
    const [popout, setPopout] = useState<boolean>(false);
    const [maxHeight, setMaxHeight] = useState<string>("");
    const [minimized, setMinimized] = useState<boolean>(false);

    const canMinimize = (props.minimizable ?? false) && !popout;
    const isMinimized = minimized && canMinimize;
    const canResize = (props.resizable ?? false) && !popout && !isMinimized;

    const computeNewMaxHeight = (node: HTMLElement) => {
        // Set max height equal to the height of the window minus rect.y

        const rect = node.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const newMaxHeight = `${Math.max(windowHeight - rect.y - 10, 200)}px`;
        setMaxHeight(newMaxHeight);
    };

    const onDrag = (_event: DraggableEvent, data: DraggableData) => {
        computeNewMaxHeight(data.node);
    };

    const onMouseDown = (_event: MouseEvent) => {
        if (!className) return;
        const node = document.getElementsByClassName(className)[0] as HTMLElement;
        computeNewMaxHeight(node);
    };

    const closeButton = (
        <NfsButton className="empty floating-window-close" onClick={closeWindow} disabled={busy}>
            <CloseIcon />
        </NfsButton>
    );

    const floatingWindow = (
        <RenderInWindow disabled={!popout} onClose={closeWindow}>
            <Draggable
                onDrag={onDrag}
                onMouseDown={onMouseDown}
                disabled={popout || backdrop}
                bounds="#root"
                defaultPosition={defaultPosition}
                nodeRef={toolRef}
                handle=".floating-window-header"
            >
                <div
                    ref={toolRef}
                    style={{ maxHeight: maxHeight, width: width }}
                    className={`floating-window-container ${className ?? ""} ${canResize ? "floating-window-resizable" : ""} ${isMinimized ? "floating-window-minimized" : ""}`}
                >
                    <div className="floating-window-header">
                        <div className="floating-window-header-info">
                            {Icon && <Icon />}
                            <span>{title ?? ""}</span>
                        </div>
                        {canMinimize ? (
                            <div className="floating-window-header-actions">
                                <NfsButton
                                    className="empty floating-window-close"
                                    onClick={() => setMinimized(!minimized)}
                                    title={minimized ? "Gjenopprett" : "Minimer"}
                                >
                                    <MinimizeIcon />
                                </NfsButton>
                                {closeButton}
                            </div>
                        ) : (
                            closeButton
                        )}
                    </div>
                    {/* `inert` is what makes this stick: it takes the whole subtree out of
                        clicking, typing and tabbing, rather than relying on every control in it
                        to have been given a disabled prop of its own. */}
                    <div
                        className={`floating-window-content ${contentClassName ?? ""} ${busy ? "floating-window-content-busy" : ""}`}
                        inert={busy}
                    >
                        {children}
                    </div>
                    <button
                        className="empty-button"
                        style={{ cursor: "default", width: "40px" }}
                        type="button"
                        title="Hidden"
                        onDoubleClick={() => setPopout(!popout)}
                    />
                </div>
            </Draggable>
        </RenderInWindow>
    );

    // The backdrop both dims the app and swallows the clicks behind it, which is what keeps a
    // modal window's underlying tool - the map, in the sector table's case - from being edited
    // out from under the draft on screen.
    return backdrop ? <div className="floating-window-backdrop">{floatingWindow}</div> : floatingWindow;
};

export default FloatingWindow;
